// Parses the per-file metadata the OneDrive backup records into the file-index
// `attributes` column (a raw JSON document). Keys MUST match the Go vocabulary
// in cmds/shared/indexing/attributes/keys.go (KeyM365*).

interface M365FileMetadata {
  created?: string; // ISO-8601, as returned by Graph
  createdBy?: string; // display name
  modifiedBy?: string; // display name
  mimeType?: string;
  etag?: string;
}

const KEY_CREATED = 'm365_created';
const KEY_CREATED_BY = 'm365_created_by';
const KEY_MODIFIED_BY = 'm365_modified_by';
const KEY_MIME_TYPE = 'm365_mime_type';
const KEY_ETAG = 'm365_etag';

export function parseM365Attributes(attributes?: string): M365FileMetadata {
  if (!attributes) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(attributes);
    if (parsed === null || typeof parsed !== 'object') {
      return {};
    }
    // Keep only string-valued keys, so non-string JSON values aren't silently
    // treated as strings (and avoids an `as` cast on the parsed result).
    const raw: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string') {
        raw[k] = v;
      }
    }
    return {
      created: raw[KEY_CREATED] || undefined,
      createdBy: raw[KEY_CREATED_BY] || undefined,
      modifiedBy: raw[KEY_MODIFIED_BY] || undefined,
      mimeType: raw[KEY_MIME_TYPE] || undefined,
      etag: raw[KEY_ETAG] || undefined,
    };
  } catch {
    // Malformed/legacy attributes — render the row without extra metadata
    // rather than breaking the explorer.
    return {};
  }
}

// Human-readable file-type label. Prefers a friendly name for common extensions
// and falls back to the uppercased extension (e.g. "PNG") or the MIME subtype.
const extensionTypeLabel: Record<string, string> = {
  doc: 'Word document',
  docx: 'Word document',
  xls: 'Excel spreadsheet',
  xlsx: 'Excel spreadsheet',
  csv: 'CSV',
  ppt: 'PowerPoint',
  pptx: 'PowerPoint',
  pdf: 'PDF',
  txt: 'Text',
  md: 'Markdown',
  png: 'PNG image',
  jpg: 'JPEG image',
  jpeg: 'JPEG image',
  gif: 'GIF image',
  svg: 'SVG image',
  mp4: 'Video',
  mov: 'Video',
  avi: 'Video',
  mp3: 'Audio',
  wav: 'Audio',
  zip: 'Archive',
  tar: 'Archive',
  gz: 'Archive',
  json: 'JSON',
  yaml: 'YAML',
  yml: 'YAML',
  html: 'HTML',
  js: 'JavaScript',
  ts: 'TypeScript',
};

interface M365EmailMetadata {
  subject?: string;
  from?: string;
  to?: string;
  date?: string;
}

const KEY_EMAIL_FROM = 'email_from';
const KEY_EMAIL_TO = 'email_to';
const KEY_EMAIL_SUBJECT = 'email_subject';
const KEY_EMAIL_DATE = 'email_date';

export function parseM365EmailAttributes(
  attributes?: string
): M365EmailMetadata {
  if (!attributes) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(attributes);
    if (parsed === null || typeof parsed !== 'object') {
      return {};
    }
    const raw: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string') {
        raw[k] = v;
      }
    }
    return {
      subject: raw[KEY_EMAIL_SUBJECT] || undefined,
      from: raw[KEY_EMAIL_FROM] || undefined,
      to: raw[KEY_EMAIL_TO] || undefined,
      date: raw[KEY_EMAIL_DATE] || undefined,
    };
  } catch {
    return {};
  }
}

export function getM365FileTypeLabel(
  fileName: string,
  mimeType?: string
): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext && extensionTypeLabel[ext]) {
    return extensionTypeLabel[ext];
  }
  if (ext && ext !== fileName.toLowerCase()) {
    return `${ext.toUpperCase()} file`;
  }
  if (mimeType) {
    const subtype = mimeType.split('/').pop();
    if (subtype) {
      return subtype.toUpperCase();
    }
  }
  return 'File';
}
